import { useCallback, useContext, useMemo, useRef } from 'react';

import {
  type AcceptSidekickConsent,
  type GetSidekickConsentStatus,
  SidekickApiContext,
  type SidekickConsentStatus,
  type SidekickConsentTerms,
} from '../contexts/SidekickApiContext';
import {
  clearCachedApiConsent,
  getCachedApiConsent,
  getStoredConsent,
  setCachedApiConsent,
  setStoredConsent,
} from '../utils/consentStorage';
import {
  recallConsentTerms,
  rememberConsentTerms,
} from '../utils/consentTermsSession';
import { useSidekickConfig } from './useSidekickConfig';

/**
 * Confirms a cache-served consent status against the backend.
 *
 * Resolves `undefined` when there was nothing to confirm — the last read
 * came from the backend already, or the localStorage fallback is in play.
 * Resolves `false` when the backend positively reports the user as no
 * longer consented, or reports terms the cached acceptance does not
 * match, having dropped the cached record on the way out. Anything
 * inconclusive (degraded response, transport failure) resolves `true`,
 * so an outage can never re-gate a consented user.
 */
export type RevalidateSidekickConsent = () => Promise<boolean | undefined>;

/**
 * Whether a terms record names the revision it was accepted under. Both
 * fields are optional on the wire, and a record missing either cannot be
 * confirmed against a later read.
 */
const hasKnownRevision = (terms?: SidekickConsentTerms): boolean =>
  Boolean(terms?.code) && Boolean(terms?.revision);

/**
 * Whether two terms records name the same revision of the same document.
 * An unknown revision on either side never matches: it proves nothing,
 * and treating it as agreement is what would let an unaccepted revision
 * pass as accepted.
 */
const isSameTerms = (
  cached?: SidekickConsentTerms,
  live?: SidekickConsentTerms
): boolean =>
  hasKnownRevision(cached) &&
  cached?.code === live?.code &&
  cached?.revision === live?.revision;

/** Resolved consent operations returned by {@link useConsentApi}. */
export type ConsentApi = {
  /**
   * Answers from the browser alone — the cached acceptance on the
   * injected path, the stored flag on the localStorage path — or returns
   * `null` when only the backend can answer. Synchronous, so a caller
   * can skip its loading state whenever no request is needed.
   */
  peekConsentStatus: () => SidekickConsentStatus | null;
  /**
   * Whether the browser holds an acceptance that
   * {@link ConsentApi.peekConsentStatus} would grant on. Unlike the peek
   * it records nothing, so a surface can read it while rendering to
   * predict where its next entry lands.
   */
  hasBrowserConsent: () => boolean;
  getConsentStatus: GetSidekickConsentStatus;
  acceptConsent: AcceptSidekickConsent;
  revalidateConsent: RevalidateSidekickConsent;
};

/**
 * Resolves the effective consent read/write callbacks the gate should
 * use. Resolved **all-or-nothing**:
 *
 * - When the host injects BOTH `getConsentStatus` and `acceptConsent`
 *   on {@link SidekickApiContext} (the ECPP CLPT-backed path), both are
 *   used verbatim, fronted by the browser cache described below.
 * - Otherwise (neither, or only one, injected) both fall back to the
 *   localStorage default keyed by `useSidekickConfig().userId`.
 *
 * Read and write must share one source of truth: mixing an injected
 * read with a localStorage write (or vice versa) would let consent
 * appear accepted in-session yet never persist, so a partial injection
 * degrades to localStorage for both rather than splitting.
 *
 * On the injected path a confirmed acceptance is cached in the browser,
 * so a returning user opens Sidekick without waiting on the consent
 * round-trip. Only a positive, authoritative, durable result carrying a
 * known terms revision is ever cached — a `degraded` read, a write the
 * backend did not persist, or a status that names no revision leaves the
 * cache empty so the next entry reads live. That keeps the gate
 * fail-closed: a cache that cannot prove consent behaves exactly like no
 * cache at all.
 *
 * The cache is deliberately outside RTK Query, whose cache does not
 * survive a page load, which is the case this exists to speed up.
 *
 * The terms an acceptance is recorded under come from the read that
 * raised the gate, which happens in a different component (and so a
 * different instance of this hook) from the one that accepts. They
 * therefore live in the shared session store rather than in a ref here.
 *
 * A caller acting on a cache-served status is expected to follow a
 * granted entry with {@link ConsentApi.revalidateConsent}, which
 * confirms it against the backend and drops the record when the backend
 * disowns the acceptance or names a revision the record does not hold.
 * `API_CONSENT_CACHE_TTL_MS` remains the backstop for the case where
 * confirmation never succeeds, such as a persistently offline client.
 *
 * Reads the API context via `useContext` (nullable) rather than
 * `useSidekickApi()` (which throws), so the resolver is safe to call
 * from surfaces that may render outside `SidekickApiProvider` — it
 * simply falls back to the local default there.
 */
export const useConsentApi = (): ConsentApi => {
  const api = useContext(SidekickApiContext);
  const injectedGetConsentStatus = api?.getConsentStatus;
  const injectedAcceptConsent = api?.acceptConsent;
  const { userId } = useSidekickConfig();
  // All-or-nothing: only use the injected API when the full read/write
  // pair is present, so the gate never reads and writes across sources.
  const useInjectedApi = Boolean(
    injectedGetConsentStatus && injectedAcceptConsent
  );
  // Whether the last status the gate acted on came from the cache, and
  // therefore still owes the backend a confirmation.
  const lastReadWasCachedRef = useRef(false);

  const peekConsentStatus = useCallback((): SidekickConsentStatus | null => {
    if (!useInjectedApi) {
      lastReadWasCachedRef.current = false;
      return { hasConsented: getStoredConsent(userId) };
    }

    const cached = getCachedApiConsent(userId);
    if (!cached) {
      return null;
    }
    const terms: SidekickConsentTerms = {
      code: cached.code,
      revision: cached.revision,
    };
    rememberConsentTerms(userId, terms);
    lastReadWasCachedRef.current = true;
    return { hasConsented: true, terms };
  }, [useInjectedApi, userId]);

  const hasBrowserConsent = useCallback(
    (): boolean =>
      useInjectedApi
        ? getCachedApiConsent(userId) !== null
        : getStoredConsent(userId),
    [useInjectedApi, userId]
  );

  const getConsentStatus = useCallback<GetSidekickConsentStatus>(async () => {
    const local = peekConsentStatus();
    if (local || !injectedGetConsentStatus) {
      return local ?? { hasConsented: false };
    }

    const status = await injectedGetConsentStatus();
    rememberConsentTerms(userId, status.terms);
    lastReadWasCachedRef.current = false;
    if (
      status.hasConsented &&
      !status.degraded &&
      hasKnownRevision(status.terms)
    ) {
      setCachedApiConsent(userId, status.terms);
    }
    return status;
  }, [peekConsentStatus, injectedGetConsentStatus, userId]);

  const revalidateConsent = useCallback<RevalidateSidekickConsent>(async () => {
    if (
      !useInjectedApi ||
      !injectedGetConsentStatus ||
      !lastReadWasCachedRef.current
    ) {
      return undefined;
    }

    // One confirmation per cache-served status. Clearing up front also
    // stops a second entry click from firing a duplicate request.
    lastReadWasCachedRef.current = false;
    // The revision the cached acceptance was recorded under, read before
    // the live status replaces it.
    const cachedTerms = recallConsentTerms(userId);

    let status: Awaited<ReturnType<GetSidekickConsentStatus>>;
    try {
      status = await injectedGetConsentStatus();
    } catch {
      // Transport failure proves nothing; keep the cached acceptance.
      return true;
    }

    if (status.degraded) {
      return true;
    }

    // The live terms are the ones a re-gate would put in front of the
    // user, so an acceptance that follows one must be cached under this
    // revision rather than the one the cache arrived with.
    rememberConsentTerms(userId, status.terms);

    if (!status.hasConsented) {
      clearCachedApiConsent(userId);
      return false;
    }

    // CLPT reports a stored ACCEPT as accepted without checking it
    // against the terms now in force, so `hasConsented` alone cannot
    // tell a current acceptance from one predating a new revision. Only
    // terms matching the cached record prove the user saw them; anything
    // else re-gates rather than refreshing the record and its TTL.
    if (!isSameTerms(cachedTerms, status.terms)) {
      clearCachedApiConsent(userId);
      return false;
    }

    setCachedApiConsent(userId, status.terms);
    return true;
  }, [useInjectedApi, injectedGetConsentStatus, userId]);

  const acceptConsent = useCallback<AcceptSidekickConsent>(async () => {
    if (useInjectedApi && injectedAcceptConsent) {
      const acceptance = await injectedAcceptConsent();
      // A bare `void` resolution means the callback cannot report
      // durability, which the contract reads as persisted.
      const isDurable =
        !acceptance ||
        (acceptance.persisted !== false && acceptance.degraded !== true);
      // The terms come from whichever instance raised the gate, which is
      // never this one: entry surfaces read, the gate component accepts.
      const acceptedTerms = recallConsentTerms(userId);
      if (isDurable && hasKnownRevision(acceptedTerms)) {
        setCachedApiConsent(userId, acceptedTerms);
      }
      return acceptance;
    }

    setStoredConsent(userId);
    return undefined;
  }, [useInjectedApi, injectedAcceptConsent, userId]);

  return useMemo(
    () => ({
      peekConsentStatus,
      hasBrowserConsent,
      getConsentStatus,
      acceptConsent,
      revalidateConsent,
    }),
    [
      peekConsentStatus,
      hasBrowserConsent,
      getConsentStatus,
      acceptConsent,
      revalidateConsent,
    ]
  );
};
